"use client";

import { cva } from "class-variance-authority";
import type { ElementType, FC, HTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "@/components/ui/link";
import { cn } from "@/lib/utils";
import type { Layout } from "@/types";
import {
  ContextNavContext,
  type ContextNavContextValue,
  useContextNav,
} from "./context-nav.context";

export type ContextNavLayout = Extract<Layout, "desktop" | "mobile">;

export interface ContextNavProps extends HTMLAttributes<HTMLElement> {
  layout?: ContextNavLayout;
  /** `value` of the `Item` that represents the current destination. */
  activeValue?: string;
  as?: ElementType;
}

const ROOT_VARIANTS = cva("", {
  variants: {
    layout: {
      desktop: "flex flex-wrap gap-2",
      mobile: "grid grid-cols-3 gap-2",
    },
  },
});

const Root: FC<ContextNavProps> = ({
  layout = "desktop",
  activeValue,
  as: Tag = "nav",
  className,
  children,
  ...props
}) => {
  const contextValue: ContextNavContextValue = { layout, activeValue };

  return (
    <ContextNavContext.Provider value={contextValue}>
      <Tag data-layout={layout} className={cn(ROOT_VARIANTS({ layout }), className)} {...props}>
        {children}
      </Tag>
    </ContextNavContext.Provider>
  );
};

interface ItemProps
  extends Omit<LinkProps, "variant" | "href" | "children" | "className" | "aria-current"> {
  /** Compared with the `activeValue` of the root. */
  value: string;
  href: string;
  /** Decorative: the label already names the destination. */
  icon?: ReactNode;
  /** Applied to the icon only while the item is active (e.g. a category color). */
  activeIconClassName?: string;
  className?: string;
  children: ReactNode;
}

const ITEM_VARIANTS = cva(
  "flex flex-col items-center justify-center rounded-lg px-2 text-center text-body-xs font-semibold leading-tight no-underline transition-colors hover:no-underline",
  {
    variants: {
      layout: {
        desktop: "min-w-22 gap-1.5 py-3",
        mobile: "gap-1 py-2.5",
      },
      active: {
        true: "bg-surface text-fg-strong shadow-md",
        false: "bg-overlay-default text-fg-on-dark hover:bg-overlay-strong",
      },
    },
    defaultVariants: { active: false },
  },
);

const Item: FC<ItemProps> = ({
  value,
  href,
  icon,
  activeIconClassName,
  className,
  children,
  ...linkProps
}) => {
  const { layout, activeValue } = useContextNav();
  const active = value === activeValue;

  return (
    <Link
      {...linkProps}
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(ITEM_VARIANTS({ layout, active }), className)}
    >
      {icon && (
        <span aria-hidden className={cn("inline-flex shrink-0", active && activeIconClassName)}>
          {icon}
        </span>
      )}
      <span>{children}</span>
    </Link>
  );
};

Root.displayName = "ContextNav";
Item.displayName = "ContextNav.Item";

export const ContextNav = Object.assign(Root, { Item });
